// client/src/components/event/RecurrenceModal.jsx
//
// Turn an event into a recurring series, or manage an existing one
// (On-Screen 2.0). Occurrences are real events, so each gets its own
// tickets / tasks / campaign.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createRecurrence, getSeries, deleteRecurrence,
  RECURRENCE_FREQS, WEEKDAYS,
} from '../../services/recurrenceApi';

export default function RecurrenceModal({ tenantId, eventId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rule, setRule] = useState({ freq: 'weekly', interval: 1, count: 8, byweekday: [] });
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSeries(tenantId, eventId);
      setSeries(res);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tenantId, eventId]);

  useEffect(() => { load(); }, [load]);

  const toggleDay = (d) => setRule((r) => ({
    ...r,
    byweekday: r.byweekday.includes(d) ? r.byweekday.filter((x) => x !== d) : [...r.byweekday, d].sort(),
  }));

  const generate = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = { freq: rule.freq, interval: Number(rule.interval) || 1, count: Number(rule.count) || 8 };
      if (rule.freq === 'weekly' && rule.byweekday.length) payload.byweekday = rule.byweekday;
      const res = await createRecurrence(tenantId, eventId, payload);
      setResult(res);
      onChanged?.();
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create the series');
    } finally {
      setBusy(false);
    }
  };

  const removeFuture = async (scope) => {
    if (!window.confirm(scope === 'all' ? 'Remove every other occurrence in this series?' : 'Remove occurrences after this one?')) return;
    setBusy(true);
    try {
      await deleteRecurrence(tenantId, eventId, scope);
      onChanged?.();
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const isSeries = series?.seriesId != null;
  const field = 'rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-md space-y-3 overflow-y-auto rounded-xl bg-white p-5 text-black dark:bg-neutral-900 dark:text-white" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold">{isSeries ? 'Recurring series' : 'Make this event recurring'}</h3>
        {error && <div className="text-sm text-red-600">{error}</div>}
        {loading && <p className="text-sm opacity-60">Loading…</p>}

        {!loading && !isSeries && !result && (
          <>
            <div className="flex gap-2">
              <select className={`${field} flex-1`} value={rule.freq} onChange={(e) => setRule((r) => ({ ...r, freq: e.target.value }))}>
                {RECURRENCE_FREQS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <label className="flex items-center gap-1 text-sm">
                <input type="number" min="1" max="104" className={`${field} w-20`} value={rule.count}
                  onChange={(e) => setRule((r) => ({ ...r, count: e.target.value }))} />
                times
              </label>
            </div>

            {rule.freq === 'weekly' && (
              <div className="flex flex-wrap gap-1">
                {WEEKDAYS.map((w, i) => (
                  <button key={w} type="button" onClick={() => toggleDay(i)}
                    className={`rounded px-2 py-1 text-xs ${rule.byweekday.includes(i) ? 'bg-cobalt text-white' : 'bg-black/5 dark:bg-white/10'}`}>
                    {w}
                  </button>
                ))}
                <span className="w-full text-xs opacity-50">Leave blank to repeat on the same weekday as this event.</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15">Cancel</button>
              <button onClick={generate} disabled={busy} className="rounded bg-cobalt px-3 py-1.5 text-sm text-white disabled:opacity-50">
                {busy ? 'Generating…' : 'Generate occurrences'}
              </button>
            </div>
          </>
        )}

        {result && (
          <div className="rounded bg-emerald-100 p-3 text-sm text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
            Created {result.generated} occurrence{result.generated === 1 ? '' : 's'}. Each is its own event — set up tickets and tasks per occurrence.
          </div>
        )}

        {!loading && isSeries && (
          <>
            <p className="text-sm opacity-70">{series.occurrences.length} occurrences in this series.</p>
            <ul className="max-h-56 space-y-1 overflow-y-auto text-sm">
              {series.occurrences.map((o) => (
                <li key={o.id} className={`flex items-center justify-between rounded px-2 py-1 ${o.is_current ? 'bg-cobalt/10' : 'bg-black/5 dark:bg-white/5'}`}>
                  <button className="truncate text-left hover:underline" onClick={() => { onClose(); navigate(`/${tenantId}/${o.id}/hub`); }}>
                    {o.startdate ? new Date(o.startdate).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : o.name}
                  </button>
                  <span className="ml-2 shrink-0 text-xs opacity-50">{o.status}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15">Close</button>
              <button onClick={() => removeFuture('following')} disabled={busy} className="rounded border border-red-400 px-3 py-1.5 text-sm text-red-600 disabled:opacity-50">
                Remove following
              </button>
              <button onClick={() => removeFuture('all')} disabled={busy} className="rounded border border-red-400 px-3 py-1.5 text-sm text-red-600 disabled:opacity-50">
                Remove all others
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
